// Lightweight Web Audio SFX engine — no audio assets needed.
class GameSounds {
  constructor() {
    this.ctx = null;
    this.muted = typeof localStorage !== "undefined" && localStorage.getItem("jd-muted") === "1";
    this.engineNodes = null;
  }

  ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
    }
    if (this.ctx.state === "suspended") {
      const now = Date.now();
      if (now - (this._lastResume || 0) > 1500) {
        this._lastResume = now;
        this.ctx.resume().catch(() => {});
      }
    }
    return this.ctx;
  }

  setMuted(m) {
    this.muted = m;
    localStorage.setItem("jd-muted", m ? "1" : "0");
    if (m) { this.engineStop(); this.stopMusic(); }
  }

  // ===== Tropical background music (procedural, no assets) =====
  startMusic() {
    if (this.muted || this._music) return;
    const ctx = this.ensure();
    if (!ctx || ctx.state !== "running") return;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 2);
    this._music = {
      gain,
      step: 0,
      nextTime: ctx.currentTime + 0.1,
      timer: setInterval(() => this._schedule(), 60),
    };
  }

  stopMusic() {
    const m = this._music;
    if (!m || !this.ctx) return;
    clearInterval(m.timer);
    m.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
    setTimeout(() => { try { m.gain.disconnect(); } catch (e) {} }, 1800);
    this._music = null;
  }

  _schedule() {
    const ctx = this.ctx;
    const m = this._music;
    if (!ctx || !m || ctx.state !== "running") return;
    const stepDur = 60 / 104 / 2; // 8th notes @104bpm
    while (m.nextTime < ctx.currentTime + 0.3) {
      this._playStep(m.step % 64, m.nextTime, m.gain);
      m.step++;
      m.nextTime += stepDur;
    }
  }

  _playStep(i, t, out) {
    // Breezy island progression: C — Am — F — G
    const chords = [
      [261.63, 329.63, 392.0],
      [220.0, 261.63, 329.63],
      [174.61, 220.0, 261.63],
      [196.0, 246.94, 293.66],
    ];
    const bar = Math.floor(i / 16) % 4;
    const chord = chords[bar];
    const sb = i % 16;
    // Soft bass on 1 and 3
    if (sb === 0 || sb === 8) this._pluck(chord[0] / 2, t, 0.55, out, "sine", 0.5);
    // Steel-drum/marimba arpeggio
    const arp = [0, 1, 2, 1, 0, 2, 1, 2, 0, 1, 2, 1, 2, 1, 0, 1];
    if (sb % 2 === 0) this._pluck(chord[arp[sb]] * 2, t, 0.3, out, "triangle", 0.42);
    // High sparkle accents
    if (sb === 4 || sb === 12) this._pluck(chord[(bar + sb) % 3] * 4, t, 0.4, out, "sine", 0.16);
    // Shaker on offbeats
    if (sb % 4 === 2) this._shaker(t, out);
  }

  _pluck(freq, t, dur, out, type, vol) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g);
    g.connect(out);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  _shaker(t, out) {
    const ctx = this.ctx;
    const dur = 0.06;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * (1 - j / d.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = 5000;
    const g = ctx.createGain();
    g.gain.value = 0.18;
    src.connect(f);
    f.connect(g);
    g.connect(out);
    src.start(t);
  }

  engine(speed) {
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx || ctx.state !== "running") return;
    if (!this.engineNodes) {
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      const osc2 = ctx.createOscillator();
      osc2.type = "triangle";
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 260;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(filter);
      osc2.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc2.start();
      gain.gain.linearRampToValueAtTime(0.035, ctx.currentTime + 0.4);
      this.engineNodes = { osc, osc2, gain };
    }
    const f = 42 + speed * 0.05;
    this.engineNodes.osc.frequency.setTargetAtTime(f, ctx.currentTime, 0.12);
    this.engineNodes.osc2.frequency.setTargetAtTime(f * 1.5, ctx.currentTime, 0.12);
  }

  engineStop() {
    if (this.engineNodes && this.ctx) {
      const { osc, osc2, gain } = this.engineNodes;
      gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.08);
      setTimeout(() => { try { osc.stop(); osc2.stop(); } catch (e) {} }, 400);
      this.engineNodes = null;
    }
  }

  blip(f0, f1, dur, type = "square", vol = 0.09) {
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx || ctx.state !== "running") return;
    const o = ctx.createOscillator();
    o.type = type;
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    const t = ctx.currentTime;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  coin() { this.blip(880, 1568, 0.13, "square", 0.06); }
  powerup() { this.blip(420, 1240, 0.3, "triangle", 0.12); }
  shieldHit() { this.blip(320, 90, 0.28, "sawtooth", 0.12); }
  jump() { this.blip(280, 920, 0.3, "sine", 0.14); }

  splash() {
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx || ctx.state !== "running") return;
    const dur = 0.25;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = 1400;
    const g = ctx.createGain();
    g.gain.value = 0.14;
    src.connect(f);
    f.connect(g);
    g.connect(ctx.destination);
    src.start();
  }

  crash() {
    this.engineStop();
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx || ctx.state !== "running") return;
    const dur = 0.5;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = 900;
    const g = ctx.createGain();
    g.gain.value = 0.28;
    src.connect(f);
    f.connect(g);
    g.connect(ctx.destination);
    src.start();
  }
}

export const gameSounds = new GameSounds();
